import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import MobileMenu from "./MobileMenu";
import hpProducts from "../data/hp-products.json";
import epsonProducts from "../data/epson-product.json";
import brotherProducts from "../data/brother-products.json";
import canonProducts from "../data/canon-products.json";

const productData = [
  ...brotherProducts,
  ...canonProducts,
  ...hpProducts,
  ...epsonProducts,
];

function HeaderOne() {
  const navigate = useNavigate();

  const [isSticky, setIsSticky] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [cartItems, setCartItems] = useState([]);
  const [cartCount, setCartCount] = useState(0);

  const [showSearch, setShowSearch] = useState(false);
  const [showCart, setShowCart] = useState(false);

  const [searchText, setSearchText] = useState("");
  const [searchResults, setSearchResults] = useState([]);

  /* Check login status on load */
  useEffect(() => {
    const loginState = sessionStorage.getItem("isLoggedIn");
    setIsLoggedIn(!!loginState);
  }, []);

  /* Load cart on mount */
  useEffect(() => {
    const updateCart = () => {
      const cart = JSON.parse(sessionStorage.getItem("cart")) || [];
      setCartItems(cart);
      setCartCount(cart.reduce((sum, item) => sum + item.quantity, 0));
    };

    updateCart();
    window.addEventListener("cart-updated", updateCart);
    return () => window.removeEventListener("cart-updated", updateCart);
  }, []);

  /* Sticky Header */
  useEffect(() => {
    const handler = () => setIsSticky(window.scrollY > 120);
    window.addEventListener("scroll", handler);
    return () => window.removeEventListener("scroll", handler);
  }, []);

  /* Search Debounce */
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!searchText.trim()) {
        setSearchResults([]);
        return;
      }
      const results = productData.filter((p) =>
        p.name.toLowerCase().includes(searchText.toLowerCase())
      );
      setSearchResults(results);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchText]);

  /* Remove Cart Item */
  const removeItem = (id) => {
    const updated = cartItems.filter((i) => i.id !== id);
    sessionStorage.setItem("cart", JSON.stringify(updated));
    setCartItems(updated);
    setCartCount(updated.reduce((s, it) => s + it.quantity, 0));
    window.dispatchEvent(new Event("cart-updated"));
  };

  const cartTotal = cartItems.reduce(
    (sum, item) => sum + item.price * item.quantity,
    0
  );

  /* Logout */
  const handleLogout = () => {
    sessionStorage.removeItem("isLoggedIn");
    sessionStorage.removeItem("cart");
    navigate("/login-now");
  };

  return (
    <>
      {/* ================= NEW HEADER ================= */}
      <header className={`new-header ${isSticky ? "sticky" : ""}`}>
        <div className="container header-inner">
          {/* LOGO */}
          <div className="logo">
            <Link to="/">
              <img src="/assets/img/logo.svg" alt="logo" />
            </Link>
          </div>

          {/* NAVIGATION */}
          <nav className="nav-links d-none d-xl-flex">
            <Link to="/">Home</Link>
            <Link to="/shop">Our Printers</Link>
            <Link to="/office-printers">Office Printers</Link>
            <Link to="/home-printers">Home Printers</Link>
            <Link to="/our-accessories">Our Accessories</Link>
            <Link to="/contact">Contact Us</Link>
          </nav>

          {/* ACTION BUTTONS */}
          <div className="header-actions">
            {/* SEARCH */}
            <button className="icon-btn" onClick={() => setShowSearch(true)}>
              <i className="fa-solid fa-magnifying-glass"></i>
            </button>

            {/* ACCOUNT */}
            <button
              className="icon-btn"
              onClick={() =>
                isLoggedIn ? navigate("/my-account") : navigate("/login-now")
              }
            >
              <i className="fa-regular fa-user"></i>
            </button>

            {/* CART */}
            <button
              className="icon-btn cart-btn"
              onClick={() => setShowCart(true)}
            >
              <i className="fa-solid fa-cart-shopping"></i>
              {cartCount > 0 && <span className="badge-new">{cartCount}</span>}
            </button>

            {/* MOBILE MENU */}
            <button
              className="menu-btn d-xl-none"
              onClick={() => setIsMobileMenuOpen(true)}
            >
              <i className="fa-solid fa-bars"></i>
            </button>
          </div>
        </div>
      </header>

      {/* ================= SEARCH DRAWER ================= */}
      {showSearch && (
        <div className="search-drawer-bg" onClick={() => setShowSearch(false)}>
          <div className="search-drawer" onClick={(e) => e.stopPropagation()}>
            <input
              autoFocus
              type="text"
              className="search-input"
              placeholder="Search products..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />

            <div className="search-results">
              {searchResults.length > 0
                ? searchResults.map((item) => (
                    <div
                      key={item.id}
                      className="search-item"
                      onClick={() => navigate(`/product-details/${item.id}`)}
                    >
                      <img src={item.imageUrl} alt="" />
                      <div>
                        <h6>{item.name}</h6>
                        <p>${item.price}</p>
                      </div>
                    </div>
                  ))
                : searchText && <p className="empty">No results found</p>}
            </div>
          </div>
        </div>
      )}

      {/* ================= CART DRAWER ================= */}
      {showCart && (
        <div className="cart-drawer-bg" onClick={() => setShowCart(false)}>
          <div className="cart-drawer" onClick={(e) => e.stopPropagation()}>
            <h4>Your Cart</h4>

            {cartItems.length === 0 ? (
              <p className="empty">Your cart is empty</p>
            ) : (
              <>
                {cartItems.map((item) => (
                  <div className="cart-row" key={item.id}>
                    <img src={item.imageUrl} alt="" />

                    <div className="cart-info">
                      <p>{item.name}</p>
                      <small>
                        {item.quantity} × ${item.price}
                      </small>
                    </div>

                    <button
                      className="remove"
                      onClick={() => removeItem(item.id)}
                    >
                      ×
                    </button>
                  </div>
                ))}

                <div className="cart-total">
                  Total: <strong>${cartTotal.toFixed(2)}</strong>
                </div>

                <button
                  className="btn-primary"
                  onClick={() => navigate("/cart")}
                >
                  View Cart
                </button>

                <button
                  className="btn-accent"
                  onClick={() => navigate("/checkout")}
                >
                  Checkout
                </button>
              </>
            )}
          </div>
        </div>
      )}

      <MobileMenu
        isOpen={isMobileMenuOpen}
        onClose={() => setIsMobileMenuOpen(false)}
      />

      {/* ================== NEW STYLES ================== */}
      <style>{`
        .new-header {
          width: 100%;
          padding: 18px 0;
          background: #ffffffdd;
          backdrop-filter: blur(14px);
          position: relative;
          transition: 0.35s ease;
          border-bottom: 1px solid #e6e9f0;
        }

        .new-header.sticky {
          position: fixed;
          top: 0;
          width: 100%;
          z-index: 9999;
          box-shadow: 0 4px 20px rgba(0,0,0,0.08);
        }

        .header-inner {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .nav-links a {
          margin: 0 18px;
          font-weight: 500;
          color: #0f172a;
          white-space: nowrap;
          transition: 0.3s;
        }

        .nav-links a:hover {
          color: #4C8BF5;
        }

        .header-actions {
          display: flex;
          align-items: center;
          gap: 18px;
        }

        .icon-btn {
          background: #eef3ff;
          border: none;
          width: 40px;
          height: 40px;
          border-radius: 12px;
          font-size: 18px;
          color: #4C8BF5;
          display: flex;
          justify-content: center;
          align-items: center;
          cursor: pointer;
          transition: 0.2s;
          position: relative;
        }

        .icon-btn:hover {
          background: #dbe7ff;
        }

        /* NEW BADGE */
        .badge-new {
          position: absolute;
          top: -6px;
          right: -8px;
          background: #ff3b30;
          color: white;
          font-size: 11px;
          font-weight: bold;
          padding: 3px 7px;
          border-radius: 8px;
          box-shadow: 0 2px 6px rgba(0,0,0,0.25);
        }

        .menu-btn {
          background: none;
          border: none;
          font-size: 22px;
          color: #0f172a;
        }

        /* SEARCH DRAWER */
        .search-drawer-bg {
          position: fixed;
          inset: 0;
          background: rgba(0,0,0,0.5);
          display: flex;
          justify-content: center;
          align-items: flex-start;
          padding-top: 100px;
          z-index: 99999;
        }

        .search-drawer {
          width: 550px;
          background: #fff;
          padding: 25px;
          border-radius: 18px;
          box-shadow: 0 8px 30px rgba(0,0,0,0.15);
        }

        .search-input {
          width: 100%;
          padding: 15px;
          border-radius: 14px;
          border: 1px solid #d1d5db;
          margin-bottom: 15px;
        }

        .search-item {
          display: flex;
          gap: 12px;
          padding: 10px;
          border-bottom: 1px solid #f1f1f1;
          cursor: pointer;
        }

        .search-item img {
          width: 100px;
          border-radius: 8px;
        }

        /* CART DRAWER */
        .cart-drawer-bg {
          position: fixed;
          inset: 0;
          background: rgba(0,0,0,0.4);
          display: flex;
          justify-content: flex-end;
          z-index: 99999;
        }

        .cart-drawer {
          width: 380px;
          background: #fff;
          height: 100vh;
          overflow-y: auto;
          padding: 20px;
          box-shadow: -6px 0 25px rgba(0,0,0,0.1);
        }

        .cart-row {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px 0;
          border-bottom: 1px solid #f1f1f1;
        }

        .cart-row img {
          width: 65px;
          border-radius: 10px;
        }

        .remove {
          background: none;
          border: none;
          font-size: 22px;
          color: #ef4444;
          margin-left: auto;
          cursor: pointer;
        }

        .btn-primary, .btn-accent {
          width: 100%;
          padding: 12px;
          margin-top: 12px;
          border-radius: 12px;
          border: none;
          color: #fff;
          cursor: pointer;
        }

        .btn-primary { background: #4C8BF5; }
        .btn-accent { background: #06B6D4; }
      `}</style>
    </>
  );
}

export default HeaderOne;
